import React, { useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link, useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { deleteMyAccount, downloadMyData } from "../account";
import Window from "./Window";
import Icon from "./Icon";

// Download everything, or delete the account for good.
export default function MyAccount() {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [typed, setTyped] = useState("");
  const [error, setError] = useState("");

  if (!user) return null;

  const download = async () => {
    setDownloading(true);
    try {
      await downloadMyData(user);
    } catch (err) {
      console.error(err);
      alert("Sorry, the download didn't work. Please try again.");
    }
    setDownloading(false);
  };

  const remove = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (typed.trim().toLowerCase() !== "delete") {
      setError('Type "delete" to confirm.');
      return;
    }
    setDeleting(true);
    try {
      await deleteMyAccount(user, password);
      navigate("/welcome", { replace: true });
    } catch (err: any) {
      console.error(err);
      setError(
        err?.code === "auth/wrong-password" || err?.code === "auth/invalid-credential"
          ? "That password isn't right."
          : err?.code === "auth/too-many-requests"
          ? "Too many tries. Please wait a few minutes."
          : "Sorry, something went wrong partway. Please try again; anything already deleted stays deleted."
      );
      setDeleting(false);
    }
  };

  return (
    <Window title="my_account.ini" tone="tan">
      <h2 className="window-heading">Your data</h2>
      <p className="mb-3 text-sm">
        Download a copy of everything your account holds: your profile, posts,
        letters, requests, kudos, and pen pals. See the{" "}
        <Link to="/privacy">privacy page</Link> for what's stored and who can
        see it.
      </p>
      <button type="button" className="btn btn-plain btn-sm" onClick={download} disabled={downloading}>
        <Icon name="save" size={14} /> {downloading ? "Gathering..." : "Download my data"}
      </button>

      <div className="divider my-5" />

      <h2 className="window-heading">Delete my account</h2>
      {!open ? (
        <>
          <p className="mb-3 text-sm">
            This permanently deletes your profile, your posts, letters you
            wrote, your requests, kudos, and pen pal connections, and your
            login. It can't be undone. You might want to download your data
            first.
          </p>
          <button type="button" className="btn btn-plain btn-sm" onClick={() => setOpen(true)}>
            <Icon name="close" size={14} /> Delete my account...
          </button>
        </>
      ) : (
        <form onSubmit={remove} className="flex flex-col gap-3">
          <p className="notice text-sm">
            <strong>This can't be undone.</strong> Letters other people wrote to
            you stay with them. Reports you sent are kept for everyone's
            safety.
          </p>
          <label className="field">
            <span className="field-label">Your password</span>
            <input
              type="password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          <label className="field">
            <span className="field-label">Type "delete" to confirm</span>
            <input
              className="input"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              required
            />
          </label>
          {error && (
            <p className="error-text" role="alert">
              {error}
            </p>
          )}
          <div className="btn-row">
            <button type="submit" className="btn" disabled={deleting}>
              {deleting ? "Deleting..." : "Delete everything"}
            </button>
            <button
              type="button"
              className="btn btn-plain"
              onClick={() => setOpen(false)}
              disabled={deleting}
            >
              Keep my account
            </button>
          </div>
        </form>
      )}
    </Window>
  );
}
